import { useEffect, useRef, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { useAuthGate } from "@/lib/authGate"
import { Wordmark } from "../ds/Beam"
import { AvatarMenu } from "../ds/NiraBar"
import { toggleTheme, useIsDark, usePageTitle, useStoredTheme } from "../answer/hooks"
import { MoonIcon, SunIcon } from "../answer/icons"
import { PRIVACY_URL as DOCUMENTS_URL } from "../privacy"
import { useReveal } from "./Reveal"
import "./site.css"

export const COMPANY = "Praxeum Intelligence"
export const CONTACT = "mailto:contact@nitiassistai.com?subject=Nira"
export const PRIVACY_POLICY_PATH = "/privacy"

/** Where the app lives when the site is served on its own (VITE_NIRA_APP_URL); empty when they
 * share an origin and the app is at "/". */
export const APP_URL = (import.meta.env.VITE_NIRA_APP_URL ?? "").replace(/\/$/, "")
/** The site's own home: the root. */
export const SITE_HOME = "/"

/** A link into the app: a router link on a shared origin, a plain link to the app's origin otherwise. */
export function AppLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  return APP_URL
    ? <a className={className} href={`${APP_URL}${to}`}>{children}</a>
    : <Link className={className} to={to}>{children}</Link>
}

/** The public site's frame (Site-Home, Site-Ethos): the nav, the page, the footer. */
export function SiteLayout({ title, children }: { title: string; children: ReactNode }) {
  const { signedIn, requireLogin } = useAuthGate()
  usePageTitle(title)
  // The reader's light or dark choice, remembered across the site and the app.
  useStoredTheme()
  const dark = useIsDark()
  useEffect(() => { window.scrollTo(0, 0) }, [title])
  // On a phone the nav is one row (wordmark · theme · menu); the menu opens a sheet (handoff 3 §10).
  // Sections marked `.nst-reveal` ease in as they come into view (Reveal.ts).
  const root = useRef<HTMLDivElement>(null)
  useReveal(root, title)
  const [sheet, setSheet] = useState(false)
  useEffect(() => { setSheet(false) }, [title])
  useEffect(() => {
    if (!sheet) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSheet(false) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [sheet])
  const themeButton = (
    <button type="button" className="nst-theme" onClick={toggleTheme} aria-label={dark ? "Switch to light theme" : "Switch to dark theme"} title={dark ? "Light theme" : "Dark theme"}>
      {dark ? <SunIcon /> : <MoonIcon />}
    </button>
  )
  const signIn = signedIn ? null : APP_URL ? (
    <a className="nst-signin" href={`${APP_URL}/auth/login`}>Sign in</a>
  ) : (
    <button type="button" className="nst-signin" onClick={() => requireLogin("login_required", "/ask")}>Sign in</button>
  )
  // Workspaces is the way into the app when signed in; a guest gets the public explainer instead of a sign-in wall.
  const workspaces = (
    <AppLink className="nst-quiet" to={signedIn ? "/workspaces" : "/workspaces/about"}>Workspaces</AppLink>
  )
  // History: the drawer of past research on the composer; only a signed-in reader has any.
  const history = signedIn ? <AppLink className="nst-quiet" to="/ask?history=1">History</AppLink> : null
  const avatar = signedIn ? <span className="nr nst-avatar"><AvatarMenu /></span> : null
  return (
    <div className="nst" ref={root}>
      <header className="nst-head">
        <div className="nst-wrap nst-nav">
          <Wordmark href={SITE_HOME} className="nst-mark" />
          <nav aria-label="Nira" className="nst-links">
            <Link to={`${SITE_HOME}#product`}>Product</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/how-we-work">How we work</Link>
            <Link to="/coverage">Coverage</Link>
            {history}
            {workspaces}
            <AppLink className="nst-btn primary sm" to="/ask">Ask a question</AppLink>
            {themeButton}
            {signIn}
            {avatar}
          </nav>
          <div className="nst-phone">
            {themeButton}
            {avatar}
            <button type="button" className="nst-menu-btn" aria-label={sheet ? "Close the menu" : "Menu"} aria-expanded={sheet} aria-controls="nst-sheet" onClick={() => setSheet((v) => !v)}>
              <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                {sheet ? <path d="M5 5l10 10M15 5 5 15" /> : <path d="M3 6h14M3 10h14M3 14h14" />}
              </svg>
            </button>
          </div>
        </div>
        {sheet ? (
          <nav id="nst-sheet" className="nst-sheet" aria-label="Nira" onClick={(e) => { if ((e.target as HTMLElement).closest("a, button")) setSheet(false) }}>
            <Link to={`${SITE_HOME}#product`}>Product</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/how-we-work">How we work</Link>
            <Link to="/coverage">Coverage</Link>
            {signIn}
            {history}
            {workspaces}
            <AppLink className="nst-btn primary" to="/ask">Ask a question</AppLink>
          </nav>
        ) : null}
      </header>
      <main>{children}</main>
      <footer className="nst-foot">
        <div className="nst-wrap nst-foot-row">
          <div>nira is research, not legal advice. Every claim links to its source.</div>
          <div className="nst-foot-links">
            <span>by {COMPANY}</span>
            <Link to="/pricing">Pricing</Link>
            <Link to="/how-we-work">How we work</Link>
            <Link to={DOCUMENTS_URL}>Your documents</Link>
            <Link to={PRIVACY_POLICY_PATH}>Privacy</Link>
            <a href={CONTACT}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

/** The two ways in: ask a question, or drop a notice (Home opens with the notice box). */
export function StartButtons({ dark = false }: { dark?: boolean }) {
  return (
    <div className="nst-ctas">
      <AppLink className={`nst-btn primary${dark ? " on-navy" : ""}`} to="/ask?focus=ask">Ask a question</AppLink>
      <AppLink className={`nst-btn${dark ? " ghost-navy" : ""}`} to="/ask?door=notice">Drop a notice</AppLink>
    </div>
  )
}
